import type { ReactNode } from 'react';
import { ParaglideMessage } from '@inlang/paraglide-js-react';
import { Icon } from '../../../components/Icon';
import { m } from '../../../paraglide/messages';

/** A keyboard-cap chip matching the palette's mock (mono, thicker bottom border). */
export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd className="mono rounded-6 border border-line border-b-[1.5px] bg-surface-2 px-1.5 py-0.5 text-11 leading-[1.35] whitespace-nowrap text-text-3">
    {children}
  </kbd>
);

export interface UseCaseSwitchProps {
  /** the current query, echoed into the box so the use-case search visibly
   *  carries over what the user has typed so far */
  query: string;
  onRun: () => void;
}

/** Pinned affordance shown while keyword-filtering: a click nudge toward the
 *  smarter use-case search, populated with the text typed so far. The query is a
 *  quoted label value (never grammatically embedded) so any input reads cleanly. */
export const UseCaseSwitch = ({ query, onRun }: UseCaseSwitchProps) => (
  <button
    type="button"
    onClick={onRun}
    className="mx-2 mt-1 mb-1.5 flex w-[calc(100%-1rem)] items-center gap-2.5 rounded-10 border border-dashed border-accent-line bg-accent-soft px-3 py-2.5 text-left"
  >
    <Icon name="sparkles" size={15} className="shrink-0 text-accent" />
    <span className="min-w-0 flex-1 truncate text-13 text-text-2">
      <ParaglideMessage
        message={m.search_use_case_switch}
        inputs={{ query }}
        markup={{
          b: ({ children }) => <b className="font-semibold text-text">{children}</b>,
          q: ({ children }) => <span className="text-text">{children}</span>,
          note: ({ children }) => <span className="text-text-3">{children}</span>,
        }}
      />
    </span>
    <Icon name="arrow-right" size={14} className="shrink-0 text-accent" />
  </button>
);
